import styles from './CurrencyInput.module.scss';

import React, { FC, useCallback, ChangeEvent, FocusEvent, useState, useEffect } from 'react';
import cx from 'clsx';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from 'i18n/numeral';

import { CURRENCY_INPUT_TEST_ID } from 'components/CurrencyInput/CurrencyInputTestIds';
import { TextField } from 'components/TextField';

import { parseWithMask } from './parseWithMask';
import { useCounter } from 'hooks/useCounter/useCounter';

type CurrencyInputProps = {
  disableAutoReset?: boolean;
  locale?: string;
  noCents?: boolean;
  className?: string;
  max?: number;
  min?: number;
  onValueChange: (value: number) => void;
  value: number;
  children?: never;
  aboveMaxContentKey?: string;
  belowMinContentKey?: string;
  onBlurOut?:(value: number) => void
}

export const CurrencyInput: FC<CurrencyInputProps> = ({
  disableAutoReset,
  noCents,
  className,
  max = Number.MAX_SAFE_INTEGER,
  min = 0,
  onValueChange,
  locale,
  value: initialValue,
  aboveMaxContentKey = 'currencyInput.aboveMax',
  belowMinContentKey = 'currencyInput.belowMin',
  onBlurOut,
  ...props
}) => {
  const step = noCents ? 1 : 0.01;
  const [counter, increment, decrement, update] = useCounter(initialValue, min, max, step);
  const [message, setMessage] = useState<string>();
  const [normalized, setNormalized] = useState(false);
  const { t } = useTranslation();
  const decimalPlaces = noCents ? 0 : 2;

  useEffect(() => {
    if (initialValue === counter.value) return;
    onValueChange(counter.value);
  }, [counter.value, initialValue, onValueChange]);

  const onKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    const key: string | undefined = event.key ?? event.keyCode?.toString();
    switch (key) {
      case 'ArrowUp':
      case '38':
        increment();
        event.preventDefault();
        event.currentTarget.value = counter.value.toFixed(decimalPlaces);
        break;
      case 'ArrowDown':
      case '40':
        decrement();
        event.preventDefault();
        event.currentTarget.value = counter.value.toFixed(decimalPlaces);
        break;
    }
  }, [counter.value, decimalPlaces, decrement, increment]);

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement> & FocusEvent<HTMLInputElement>) => {
    if (event.target.value === '') return;

    const masked = parseWithMask(event.target.value, decimalPlaces);

    setMessage(() => {
      if (masked > max) return t(aboveMaxContentKey, { max });
      if (masked < min) return t(belowMinContentKey, { min });

      return undefined;
    });

    setNormalized(false);

    if (event.type === 'blur') {
      const value = Math.max(min, Math.min(max, masked));

      // eslint-disable-next-line functional/immutable-data
      event.target.value = value.toFixed(decimalPlaces);

      setNormalized(value !== masked);
      onBlurOut?.(value);
    } else {
      // eslint-disable-next-line functional/immutable-data
      event.target.value = masked.toFixed(decimalPlaces);
    }

    update(parseFloat(event.target.value));
  }, [aboveMaxContentKey, belowMinContentKey, decimalPlaces, max, min, onBlurOut, t, update]);

  const onBlur = useCallback((event: FocusEvent<HTMLElement>) => {
    if (disableAutoReset) return;
    const target = event.target as HTMLInputElement;
    if (target.value === '') {
      // eslint-disable-next-line functional/immutable-data
      target.value = Math.max(min, Math.min(max, counter.value)).toFixed(decimalPlaces);
    } else {
      onChange(event as FocusEvent<HTMLInputElement>);
    }
  }, [decimalPlaces, disableAutoReset, max, min, onChange, counter.value]);

  // TODO: Support comma decimal separator for locales that use it
  // The 'type="number"' HTML specification defines the decimal separator as a period regardless of the locale
  // const decimalSeparator = lookupNumeralLocale(locale).delimiters.decimal;
  return (
    <TextField
      className={cx(styles.currencyInput, className)}
      icon={<span>{getCurrencySymbol(locale)}</span>}
      max={max}
      min={min}
      step={`${step}`}
      onBlur={onBlur}
      onChange={onChange}
      onKeyDown={onKeyPress}
      onKeyUp={onKeyPress}
      data-testid={CURRENCY_INPUT_TEST_ID.ROOT}
      type="number"
      defaultValue={initialValue.toFixed(decimalPlaces)}
      inputProps={{ 'data-testid': CURRENCY_INPUT_TEST_ID.INPUT }}
      errorMessage={!disableAutoReset ? undefined : message }
      required
      hideErrorText={!disableAutoReset}
      helpText={disableAutoReset || !normalized ? undefined : message}
      {...props}
    />
  );
};
